import { z } from 'zod';
import { REFERRAL_EDIT_KINDS, ReferralEditKind } from '../dto/my-referrals';
import { REFERRAL_STATUS_LABELS, ReferralStatus } from '../enums';
import { REFERRAL_EDIT_LABELS } from '../referral-edits';
import { renderEmail, type EmailContent, type RenderedEmail } from './layout';

const name = z.string().trim().min(1).max(200);
const referenceNumber = z.string().regex(/^GTR-\d{4}-\d{5,}$/);

const referralChange = {
  referenceNumber,
  trusteeName: name,
  familyLastName: name,
  cycleLabel: name,
  reviewUrl: z.string().url(),
};
type ReferralChangeData = { referenceNumber: string; trusteeName: string; familyLastName: string; cycleLabel: string; reviewUrl: string };
const changeSample: ReferralChangeData = {
  referenceNumber: 'GTR-2026-01001',
  trusteeName: 'Alex Morgan',
  familyLastName: 'Rivera',
  cycleLabel: '2026-27',
  reviewUrl: 'https://hv.example.org/pipeline?list=trustee&h=00000000-0000-4000-8000-000000000000',
};
const applicantCount = z.number().int().min(1).max(10);
const applicantNoun = (n: number) => (n === 1 ? '1 applicant' : `${n} applicants`);
const referralAbout = (d: ReferralChangeData) => `referral ${d.referenceNumber}: the ${d.familyLastName} family, ${d.cycleLabel}`;
const changeBody = (d: ReferralChangeData, ...lines: string[]) =>
  [...lines, 'HV was updated with the change and the household is queued for scoring if its scoring input changed.', `Open it in HV: ${d.reviewUrl}`].join('\n\n');

export const EMAIL_TEMPLATES = {
  user_invite: {
    label: 'User invitation',
    audience: 'Invited user',
    data: z.object({
      displayName: name,
      invitedByName: name,
      signInUrl: z.string().url(),
    }),
    sample: { displayName: 'Alex Morgan', invitedByName: 'Jordan Lee', signInUrl: 'https://hv.example.org/login' },
    compose: (d: { displayName: string; invitedByName: string; signInUrl: string }): EmailContent => ({
      subject: 'You have been invited to the Gulliver Prep HV Platform',
      bodyText: [
        `Hello ${d.displayName},`,
        `${d.invitedByName} has given you access to the Gulliver Prep HV Platform.`,
        `Sign in here: ${d.signInUrl}`,
        'If you were not expecting this invitation, you can ignore this email.',
      ].join('\n\n'),
    }),
  },
  test_email: {
    label: 'Test email',
    audience: 'The administrator who sent the test',
    data: z.object({
      requestedByName: name,
    }),
    sample: { requestedByName: 'Jordan Lee' },
    compose: (d: { requestedByName: string }): EmailContent => ({
      subject: 'Test email from the Gulliver Prep HV Platform',
      bodyText: [
        `Hello ${d.requestedByName},`,
        'You asked for a test email from Admin → Notifications. If you can read this, email delivery from the HV Platform is working.',
        'Check the sender name and reply-to address of this message against the notification settings.',
      ].join('\n\n'),
    }),
  },
  login_code: {
    label: 'Sign-in code',
    audience: 'Trustee signing in',
    sensitive: true,
    data: z.object({
      displayName: name,
      code: z.string().regex(/^\d{6}$/),
      expiresInMinutes: z.number().int().min(1).max(60),
      signInUrl: z.string().url(),
    }),
    sample: { displayName: 'Alex Morgan', code: '123456', expiresInMinutes: 15, signInUrl: 'https://hv.example.org/login' },
    compose: (d: { displayName: string; code: string; expiresInMinutes: number; signInUrl: string }): EmailContent => ({
      subject: 'Your Gulliver Prep HV Platform sign-in code',
      bodyText: [
        `Hello ${d.displayName},`,
        `Your sign-in code is: ${d.code}`,
        `Enter it on the sign-in page within ${d.expiresInMinutes} minutes. It works once. Sign-in page: ${d.signInUrl}`,
        'If you did not ask for this code, you can ignore this email. Never share the code with anyone.',
      ].join('\n\n'),
    }),
  },
  access_request_new: {
    label: 'New sign-in request',
    audience: 'Administrators',
    data: z.object({
      requesterName: name,
      requesterEmail: z.string().email(),
      reviewUrl: z.string().url(),
    }),
    sample: { requesterName: 'Sam Rivera', requesterEmail: 'sam.rivera@example.org', reviewUrl: 'https://hv.example.org/admin/access-requests' },
    compose: (d: { requesterName: string; requesterEmail: string; reviewUrl: string }): EmailContent => ({
      subject: 'New sign-in request for the Gulliver Prep HV Platform',
      bodyText: [
        `${d.requesterName} (${d.requesterEmail}) signed in with a school Google account but has not been invited.`,
        'They cannot use the platform until an administrator approves the request.',
        `Approve or deny it on the Sign-in requests page: ${d.reviewUrl}`,
      ].join('\n\n'),
    }),
  },
  access_request_reminder: {
    label: 'Sign-in request reminder',
    audience: 'Administrators',
    data: z.object({
      requesterName: name,
      requesterEmail: z.string().email(),
      requestedOn: z.string().trim().min(1).max(40),
      reviewUrl: z.string().url(),
    }),
    sample: {
      requesterName: 'Sam Rivera',
      requesterEmail: 'sam.rivera@example.org',
      requestedOn: '2026-10-01',
      reviewUrl: 'https://hv.example.org/admin/access-requests',
    },
    compose: (d: { requesterName: string; requesterEmail: string; requestedOn: string; reviewUrl: string }): EmailContent => ({
      subject: 'Reminder: a sign-in request is waiting for approval',
      bodyText: [
        `${d.requesterName} (${d.requesterEmail}) tried to sign in again and is still waiting for approval. The request was made on ${d.requestedOn}.`,
        `Approve or deny it on the Sign-in requests page: ${d.reviewUrl}`,
      ].join('\n\n'),
    }),
  },
  access_request_approved: {
    label: 'Sign-in request approved',
    audience: 'Requester',
    data: z.object({
      displayName: name,
      signInUrl: z.string().url(),
    }),
    sample: { displayName: 'Sam Rivera', signInUrl: 'https://hv.example.org/login' },
    compose: (d: { displayName: string; signInUrl: string }): EmailContent => ({
      subject: 'Your access to the Gulliver Prep HV Platform is approved',
      bodyText: [`Hello ${d.displayName},`, 'Your request to use the Gulliver Prep HV Platform has been approved.', `Sign in here: ${d.signInUrl}`].join('\n\n'),
    }),
  },
  access_request_denied: {
    label: 'Sign-in request denied',
    audience: 'Requester',
    data: z.object({
      displayName: name,
      reason: z.string().trim().min(1).max(1000),
    }),
    sample: { displayName: 'Sam Rivera', reason: 'The HV Platform is limited to the Admissions team.' },
    compose: (d: { displayName: string; reason: string }): EmailContent => ({
      subject: 'Your request to use the Gulliver Prep HV Platform',
      bodyText: [
        `Hello ${d.displayName},`,
        'Your request to use the Gulliver Prep HV Platform was not approved.',
        `Reason: ${d.reason}`,
        'If you think this is a mistake, contact the Admissions office.',
      ].join('\n\n'),
    }),
  },
  trustee_confirmation: {
    label: 'Referral received',
    audience: 'Trustee who submitted',
    data: z.object({
      displayName: name,
      referenceNumber,
      familyLastName: name,
      cycleLabel: name,
      referralUrl: z.string().url(),
    }),
    sample: {
      displayName: 'Alex Morgan',
      referenceNumber: 'GTR-2026-01001',
      familyLastName: 'Rivera',
      cycleLabel: '2026-27',
      referralUrl: 'https://hv.example.org/my-referrals/00000000-0000-4000-8000-000000000000',
    },
    compose: (d: { displayName: string; referenceNumber: string; familyLastName: string; cycleLabel: string; referralUrl: string }): EmailContent => ({
      subject: `Referral ${d.referenceNumber} received`,
      bodyText: [
        `Hello ${d.displayName},`,
        `Thank you. The Office of Admission received your referral of the ${d.familyLastName} family for ${d.cycleLabel}. Your reference number is ${d.referenceNumber}.`,
        `Sign in to see its status: ${d.referralUrl}`,
        'A referral provides context to Admissions; it does not guarantee admission.',
      ].join('\n\n'),
    }),
  },
  admissions_new_submission: {
    label: 'New trustee referral',
    audience: 'Admissions',
    data: z.object({
      referenceNumber,
      trusteeName: name,
      familyLastName: name,
      cycleLabel: name,
      children: z.number().int().min(1).max(10),
      reviewUrl: z.string().url(),
    }),
    sample: {
      referenceNumber: 'GTR-2026-01001',
      trusteeName: 'Alex Morgan',
      familyLastName: 'Rivera',
      cycleLabel: '2026-27',
      children: 2,
      reviewUrl: 'https://hv.example.org/pipeline?list=trustee&h=00000000-0000-4000-8000-000000000000',
    },
    compose: (d: { referenceNumber: string; trusteeName: string; familyLastName: string; cycleLabel: string; children: number; reviewUrl: string }): EmailContent => ({
      subject: `New trustee referral ${d.referenceNumber}`,
      bodyText: [
        `${d.trusteeName} submitted referral ${d.referenceNumber}: the ${d.familyLastName} family, ${d.children} ${d.children === 1 ? 'child' : 'children'}, ${d.cycleLabel}.`,
        'Status: Draft → Submitted. The family is on the Trustee Recommendations list and is queued for scoring.',
        `Open it in HV: ${d.reviewUrl}`,
      ].join('\n\n'),
    }),
  },
  applicant_added: {
    label: 'Applicant added to a referral',
    audience: 'Admissions',
    data: z.object({ ...referralChange, applicants: applicantCount }),
    sample: { ...changeSample, applicants: 1 },
    compose: (d: ReferralChangeData & { applicants: number }): EmailContent => ({
      subject: `Applicant added to referral ${d.referenceNumber}`,
      bodyText: changeBody(d, `${d.trusteeName} added ${applicantNoun(d.applicants)} to ${referralAbout(d)}.`),
    }),
  },
  applicant_info_updated: {
    label: 'Applicant information updated',
    audience: 'Admissions',
    data: z.object({ ...referralChange, applicants: applicantCount }),
    sample: { ...changeSample, applicants: 1 },
    compose: (d: ReferralChangeData & { applicants: number }): EmailContent => ({
      subject: `Applicant information updated on referral ${d.referenceNumber}`,
      bodyText: changeBody(d, `${d.trusteeName} updated the name, grade, school or location of ${applicantNoun(d.applicants)} on ${referralAbout(d)}.`),
    }),
  },
  screening_responses_updated: {
    label: 'Screening responses updated',
    audience: 'Admissions',
    data: z.object({ ...referralChange, applicants: applicantCount }),
    sample: { ...changeSample, applicants: 2 },
    compose: (d: ReferralChangeData & { applicants: number }): EmailContent => ({
      subject: `Screening responses updated on referral ${d.referenceNumber}`,
      bodyText: changeBody(d, `${d.trusteeName} updated the academic or disciplinary screening responses for ${applicantNoun(d.applicants)} on ${referralAbout(d)}.`),
    }),
  },
  family_responses_updated: {
    label: 'Family responses updated',
    audience: 'Admissions',
    data: z.object(referralChange),
    sample: changeSample,
    compose: (d: ReferralChangeData): EmailContent => ({
      subject: `Family responses updated on referral ${d.referenceNumber}`,
      bodyText: changeBody(d, `${d.trusteeName} updated the family responses or the recommendation flag on ${referralAbout(d)}.`),
    }),
  },
  recommendation_resubmitted: {
    label: 'Referral resubmitted',
    audience: 'Admissions',
    data: z.object({ ...referralChange, fromStatus: ReferralStatus, changes: z.array(ReferralEditKind).max(REFERRAL_EDIT_KINDS.length) }),
    sample: { ...changeSample, fromStatus: 'needs_clarification', changes: ['applicant_info_updated', 'family_responses_updated'] as ReferralEditKind[] },
    compose: (d: ReferralChangeData & { fromStatus: ReferralStatus; changes: ReferralEditKind[] }): EmailContent => ({
      subject: `Referral ${d.referenceNumber} resubmitted`,
      bodyText: changeBody(
        d,
        `${d.trusteeName} edited and resubmitted ${referralAbout(d)}.`,
        `Status: ${REFERRAL_STATUS_LABELS[d.fromStatus]} → ${REFERRAL_STATUS_LABELS.submitted}.`,
        d.changes.length ? `Changes: ${d.changes.map((c) => REFERRAL_EDIT_LABELS[c]).join('; ')}.` : 'No answers were changed.',
      ),
    }),
  },
  clarification_requested: {
    label: 'Clarification requested',
    audience: 'Trustee who submitted',
    data: z.object({
      displayName: name,
      referenceNumber,
      familyLastName: name,
      referralUrl: z.string().url(),
    }),
    sample: {
      displayName: 'Alex Morgan',
      referenceNumber: 'GTR-2026-01001',
      familyLastName: 'Rivera',
      referralUrl: 'https://hv.example.org/my-referrals/00000000-0000-4000-8000-000000000000',
    },
    compose: (d: { displayName: string; referenceNumber: string; familyLastName: string; referralUrl: string }): EmailContent => ({
      subject: `Clarification requested for referral ${d.referenceNumber}`,
      bodyText: [
        `Hello ${d.displayName},`,
        `The Office of Admission has a question about your referral ${d.referenceNumber} of the ${d.familyLastName} family.`,
        `Sign in to read the request and respond: ${d.referralUrl}`,
        'This email leaves out the details of the referral and of the request on purpose.',
      ].join('\n\n'),
    }),
  },
  clarification_response: {
    label: 'Clarification response',
    audience: 'Admissions',
    data: z.object(referralChange),
    sample: { ...changeSample, reviewUrl: 'https://hv.example.org/referrals?h=00000000-0000-4000-8000-000000000000&r=00000000-0000-4000-8000-000000000001' },
    compose: (d: ReferralChangeData): EmailContent => ({
      subject: `Clarification received for referral ${d.referenceNumber}`,
      bodyText: [
        `${d.trusteeName} answered the clarification request on ${referralAbout(d)}.`,
        `Status: ${REFERRAL_STATUS_LABELS.needs_clarification} → ${REFERRAL_STATUS_LABELS.submitted}.`,
        'The response is in the referral’s status history and in the family’s Activity & Notes; this email leaves it out on purpose.',
        `Open it in HV: ${d.reviewUrl}`,
      ].join('\n\n'),
    }),
  },
  admissions_status_changed: {
    label: 'Referral status changed',
    audience: 'Admissions',
    data: z.object({
      referenceNumber,
      familyLastName: name,
      trusteeName: name,
      fromStatus: ReferralStatus,
      toStatus: ReferralStatus,
      changedByName: name,
      reviewUrl: z.string().url(),
    }),
    sample: {
      referenceNumber: 'GTR-2026-01001',
      familyLastName: 'Rivera',
      trusteeName: 'Alex Morgan',
      fromStatus: 'submitted' as const,
      toStatus: 'under_review' as const,
      changedByName: 'Jordan Lee',
      reviewUrl: 'https://hv.example.org/referrals?h=00000000-0000-4000-8000-000000000000',
    },
    compose: (d: {
      referenceNumber: string;
      familyLastName: string;
      trusteeName: string;
      fromStatus: ReferralStatus;
      toStatus: ReferralStatus;
      changedByName: string;
      reviewUrl: string;
    }): EmailContent => ({
      subject: `Referral ${d.referenceNumber} is now ${REFERRAL_STATUS_LABELS[d.toStatus]}`,
      bodyText: [
        `${d.changedByName} changed the status of referral ${d.referenceNumber} (the ${d.familyLastName} family, referred by ${d.trusteeName}).`,
        `Status: ${REFERRAL_STATUS_LABELS[d.fromStatus]} → ${REFERRAL_STATUS_LABELS[d.toStatus]}.`,
        `Open it in HV: ${d.reviewUrl}`,
      ].join('\n\n'),
    }),
  },
} as const;

export const SCRUBBED_EMAIL_BODY = '[Removed after delivery: this email contained a one-time sign-in code.]';

export const SENSITIVE_NOTIFICATION_KINDS = Object.entries(EMAIL_TEMPLATES)
  .filter(([, t]) => 'sensitive' in t && t.sensitive)
  .map(([kind]) => kind);

export function isSensitiveNotificationKind(kind: string): boolean {
  return SENSITIVE_NOTIFICATION_KINDS.includes(kind);
}

export type NotificationKind = keyof typeof EMAIL_TEMPLATES;
export const NOTIFICATION_KINDS = Object.keys(EMAIL_TEMPLATES) as [NotificationKind, ...NotificationKind[]];
export const NotificationKind = z.enum(NOTIFICATION_KINDS);

export type EmailTemplateData<K extends NotificationKind> = z.input<(typeof EMAIL_TEMPLATES)[K]['data']>;

export function notificationKindLabel(kind: string): string {
  return (EMAIL_TEMPLATES as Record<string, { label: string }>)[kind]?.label ?? kind.replace(/_/g, ' ');
}

export function composeEmail<K extends NotificationKind>(kind: K, data: EmailTemplateData<K>): EmailContent {
  const template: { data: z.ZodType; compose: (d: never) => EmailContent } = EMAIL_TEMPLATES[kind];
  return (template.compose as (d: unknown) => EmailContent)(template.data.parse(data));
}

export function renderTemplate<K extends NotificationKind>(kind: K, data: EmailTemplateData<K>, logoSrc?: string): RenderedEmail {
  return renderEmail(composeEmail(kind, data), logoSrc);
}
